/**
 * Accueil parent (migration 0348) : la semaine de chaque enfant lié, avec les
 * MÊMES chiffres que le bilan du dimanche (0287, `parent_digest_child_stats`,
 * fenêtre glissante de 7 jours). Les helpers de présentation sont purs et
 * testés (`overview.test.ts`).
 *
 * RPC postérieure aux types générés → client générique (cf. subscriptions.ts).
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export type ChildWeekOverview = {
  student_id: string;
  first_name: string;
  level: string | null;
  last_activity_at: string | null;
  exercises_completed: number;
  exercises_prev_week: number;
  avg_score: number | null;
  xp: number;
  hora: number;
  cards_worked: number;
  cards_mastered: number;
  cards_to_work: number;
  streak_days: number;
  top_cards: { title: string; hora: number }[];
};

const num = (v: unknown) => (v == null || !Number.isFinite(Number(v)) ? 0 : Number(v));

/** Normalise une ligne de la RPC (numeric → number, champs manquants → 0). */
export function toChildWeekOverview(raw: Record<string, unknown>): ChildWeekOverview {
  const top = Array.isArray(raw.top_cards) ? (raw.top_cards as Record<string, unknown>[]) : [];
  return {
    student_id: String(raw.student_id ?? ''),
    first_name: String(raw.first_name ?? '') || 'Ton enfant',
    level: typeof raw.level === 'string' ? raw.level : null,
    last_activity_at: typeof raw.last_activity_at === 'string' ? raw.last_activity_at : null,
    exercises_completed: num(raw.exercises_completed),
    exercises_prev_week: num(raw.exercises_prev_week),
    avg_score: raw.avg_score == null ? null : num(raw.avg_score),
    xp: num(raw.xp),
    hora: num(raw.hora),
    cards_worked: num(raw.cards_worked),
    cards_mastered: num(raw.cards_mastered),
    cards_to_work: num(raw.cards_to_work),
    streak_days: num(raw.streak_days),
    top_cards: top.map((t) => ({ title: String(t.title ?? 'SKILLCARD'), hora: num(t.hora) })),
  };
}

export function useParentChildrenOverview(parentId: string) {
  return useQuery({
    queryKey: ['parent-children-overview', parentId],
    enabled: !!parentId,
    staleTime: 60_000,
    queryFn: async (): Promise<ChildWeekOverview[]> => {
      const { data, error } = await db.rpc('get_parent_children_overview');
      if (error) throw new Error(error.message);
      return ((data ?? []) as Record<string, unknown>[]).map(toChildWeekOverview);
    },
  });
}

// ─── Présentation (pur) ─────────────────────────────────────────────────────

const DAY_MS = 86_400_000;

/** Jours calendaires (heure locale) depuis la dernière activité (null = jamais). */
export function daysSince(iso: string | null, now: Date = new Date()): number | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (!Number.isFinite(then.getTime())) return null;
  const day = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.max(0, Math.round((day(now) - day(then)) / DAY_MS));
}

/** « aujourd’hui », « hier », « il y a 5 jours », « jamais ». */
export function lastActivityLabel(iso: string | null, now: Date = new Date()): string {
  const d = daysSince(iso, now);
  if (d == null) return 'jamais';
  if (d === 0) return 'aujourd’hui';
  if (d === 1) return 'hier';
  return `il y a ${d} jours`;
}

export type WeekTone = 'good' | 'neutral' | 'alert';

/**
 * La phrase qui résume la semaine, en tête de la carte enfant — c'est
 * l'information n°1 que le parent vient chercher : « est-ce qu'il travaille ? ».
 * `alert` = rien depuis 5 jours ou plus (ou jamais) : un encouragement aide.
 */
export function weekHeadline(
  c: ChildWeekOverview,
  now: Date = new Date(),
): { tone: WeekTone; text: string } {
  const n = c.exercises_completed;
  const prev = c.exercises_prev_week;
  if (n > 0) {
    const ex = `${n} exercice${n > 1 ? 's' : ''} terminé${n > 1 ? 's' : ''} cette semaine`;
    if (n > prev && prev > 0) return { tone: 'good', text: `${ex} (${prev} la semaine d’avant)` };
    if (n < prev) return { tone: 'neutral', text: `${ex} (${prev} la semaine d’avant)` };
    return { tone: 'good', text: ex };
  }
  const d = daysSince(c.last_activity_at, now);
  if (d == null) return { tone: 'alert', text: `${c.first_name} n’a pas encore commencé.` };
  if (d < 5)
    return {
      tone: 'neutral',
      text: `Pas d’exercice terminé cette semaine, actif ${lastActivityLabel(c.last_activity_at, now)}.`,
    };
  return {
    tone: 'alert',
    text: `Aucune activité depuis ${d} jours : un petit encouragement peut aider.`,
  };
}

/** Ordre d'affichage : d'abord les enfants à relancer, puis l'ordre de liaison. */
export function sortForAttention(list: ChildWeekOverview[], now: Date = new Date()) {
  const rank = (c: ChildWeekOverview) => (weekHeadline(c, now).tone === 'alert' ? 0 : 1);
  return list
    .map((c, i) => ({ c, i }))
    .sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i)
    .map(({ c }) => c);
}
